let audioCtx = null;
let masterGain = null;

const sfxCache = new Map();
const sfxLoading = new Map();

let currentBgm = null;
let currentBgmName = null;

const PRELOAD_SFX = [
  'button-click',
  'gamble-flip',
  'score-up',
  'short-circuit',
  'gamble-win',
  'gamble-lose',
  'bomb-explode',
  'combo-win',
  'ticking-clock',
  'heart-stop-heartbeat',
];

const BGM_VOLUME = {
  'game-bgm': 0.15,
};

function getAudioContextCtor() {
  if (typeof window === 'undefined') return null;
  return window.AudioContext || window.webkitAudioContext;
}

export function initAudio() {
  if (typeof window === 'undefined') return;

  if (audioCtx) {
    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
    return;
  }

  const AudioContextCtor = getAudioContextCtor();
  if (!AudioContextCtor) return;

  audioCtx = new AudioContextCtor();
  masterGain = audioCtx.createGain();
  masterGain.gain.value = 0.6;
  masterGain.connect(audioCtx.destination);

  PRELOAD_SFX.forEach(name => loadSfx(name).catch(() => {}));
}

function sfxUrl(name) {
  return `/sfx/${name}.mp3`;
}

function loadSfx(name) {
  const cached = sfxCache.get(name);
  if (cached) return Promise.resolve(cached);

  const inflight = sfxLoading.get(name);
  if (inflight) return inflight;

  const promise = fetch(sfxUrl(name))
    .then(res => {
      if (!res.ok) throw new Error(`SFX fetch failed: ${name}`);
      return res.arrayBuffer();
    })
    .then(buf => {
      if (!audioCtx) throw new Error('AudioContext is not initialized');
      return audioCtx.decodeAudioData(buf);
    })
    .then(decoded => {
      sfxCache.set(name, decoded);
      sfxLoading.delete(name);
      return decoded;
    })
    .catch(err => {
      sfxLoading.delete(name);
      throw err;
    });

  sfxLoading.set(name, promise);
  return promise;
}

function playBuffer(buf, gain = 1, playbackRate = 1) {
  if (!audioCtx || !masterGain) return;
  if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});

  const source = audioCtx.createBufferSource();
  source.buffer = buf;
  source.playbackRate.value = playbackRate;

  if (gain !== 1) {
    const gainNode = audioCtx.createGain();
    gainNode.gain.value = gain;
    source.connect(gainNode).connect(masterGain);
  } else {
    source.connect(masterGain);
  }

  source.start();
}

export function playSfx(name, gain = 1, playbackRate = 1) {
  initAudio();
  if (!audioCtx) return;

  const cached = sfxCache.get(name);
  if (cached) {
    playBuffer(cached, gain, playbackRate);
    return;
  }

  loadSfx(name)
    .then(buf => playBuffer(buf, gain, playbackRate))
    .catch(() => {});
}

export function playLoopSfx(name, gain = 1) {
  initAudio();
  if (!audioCtx || !masterGain) return { stop: () => {} };

  let stopped = false;
  let source = null;

  const start = (buf) => {
    if (stopped || !audioCtx || !masterGain) return;
    source = audioCtx.createBufferSource();
    source.buffer = buf;
    source.loop = true;

    if (gain !== 1) {
      const gainNode = audioCtx.createGain();
      gainNode.gain.value = gain;
      source.connect(gainNode).connect(masterGain);
    } else {
      source.connect(masterGain);
    }

    source.start();
  };

  const cached = sfxCache.get(name);
  if (cached) start(cached);
  else loadSfx(name).then(start).catch(() => {});

  return {
    stop: () => {
      stopped = true;
      if (source) {
        try { source.stop(); } catch {}
        source = null;
      }
    },
  };
}

function createBgmAudio(name) {
  const audio = new Audio(`/sfx/${name}.mp3`);
  audio.loop = true;
  audio.volume = BGM_VOLUME[name] ?? 0.2;
  return audio;
}

export function playBgm(name) {
  if (typeof window === 'undefined') return;

  if (currentBgmName === name && currentBgm) {
    if (currentBgm.paused) currentBgm.play().catch(() => {});
    return;
  }

  stopBgm();
  currentBgm = createBgmAudio(name);
  currentBgmName = name;
  currentBgm.play().catch(() => {});
}

export function stopBgm() {
  if (!currentBgm) return;
  currentBgm.pause();
  currentBgm.src = '';
  currentBgm = null;
  currentBgmName = null;
}

export function cleanupAudio() {
  stopBgm();
  sfxCache.clear();
  sfxLoading.clear();
  if (audioCtx) {
    audioCtx.close().catch(() => {});
    audioCtx = null;
    masterGain = null;
  }
}
